import { ReactNode, useEffect } from 'react';
import { Box, Dot, Icon, Link, Skeleton, SkeletonBox, Tag, Text } from '@bitrise/bitkit';
import { formatDate } from '@/utils/commonFormats';
import useChangelog, { ChangelogTag } from '@/hooks/useChangelog';
import { segmentTrack } from '@/hooks/analytics/useTracking';

const getChangelogTopicColor = (isNew: boolean | undefined) => {
  return isNew ? 'sys.interactive.bold' : 'sys.neutral.moderate';
};

type TagColorScheme = 'purpleFilled' | 'purple' | 'blue' | 'green' | 'red' | 'yellow' | 'neutral' | undefined;

const changelogTagMapping: Record<ChangelogTag, { title: string; color: TagColorScheme }> = {
  'new-feature': { title: `🎉 New feature`, color: 'purpleFilled' },
  'feature-update': { title: 'Feature update', color: 'purple' },
  'step-update': { title: 'Step update', color: 'blue' },
  'api-update': { title: 'API update', color: 'green' },
  deprecation: { title: 'Deprecation', color: 'yellow' },
};

const getChangelogTag = (tags: ChangelogTag[]): ReactNode | null => {
  let tagConfig = null;
  if (tags.includes('new-feature')) tagConfig = changelogTagMapping['new-feature'];
  else if (tags.includes('feature-update')) tagConfig = changelogTagMapping['feature-update'];
  else if (tags.includes('step-update')) tagConfig = changelogTagMapping['step-update'];
  else if (tags.includes('api-update')) tagConfig = changelogTagMapping['api-update'];
  else if (tags.includes('deprecation')) tagConfig = changelogTagMapping.deprecation;
  return (
    tagConfig && (
      <>
        <Tag size="sm" colorScheme={tagConfig.color} height={24}>
          {tagConfig.title}
        </Tag>
        <Text textStyle="body/md/regular" color="text.secondary">
          &bull;
        </Text>
      </>
    )
  );
};

const ChangelogDrawer = (): JSX.Element => {
  const { topics: changelogTopics, isLoading, setChangelogLastVisitCookie } = useChangelog();

  useEffect(() => setChangelogLastVisitCookie(new Date()));

  return (
    <Box display="flex" flexDirection="column">
      <Text textStyle="heading/h4" paddingX="24" marginY="8">
        What&apos;s new on Bitrise
      </Text>
      {isLoading &&
        [...Array(5)].map((id) => (
          <Skeleton key={id} isActive overflow="hidden" paddingY="8" paddingX="24">
            <SkeletonBox height="24px" width="80%" marginBottom="4" />
            <SkeletonBox height="16px" width="70%" marginBottom="4" />
          </Skeleton>
        ))}
      {changelogTopics?.slice(0, 5).map((topic) => (
        <Link
          key={topic.slug}
          isExternal
          href={
            topic.id === 0
              ? `https://bitrise.io/changelog/${topic.slug}`
              : `https://bitrise.io/changelog/${topic.slug}/${topic.id}`
          }
          _hover={{ backgroundColor: 'neutral.95' }}
          paddingY="8"
          paddingX="24"
          display="flex"
          gap="16"
          onClick={() => segmentTrack('Changelog Drawer Topic Name Clicked', { changelog_topic_name: topic.title })}
        >
          <Dot size={8} backgroundColor={getChangelogTopicColor(topic.isNew)} marginY="8" flexShrink="0" />
          <Box>
            <Text textColor="neutral.20" fontSize="3">
              {topic.title}
            </Text>
            <Box display="flex" alignItems="center" gap={5} marginTop={4}>
              {topic.tags && getChangelogTag(topic.tags)}
              <Text textStyle="body/md/regular" color="text.secondary">
                <time dateTime={topic.created_at} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatDate(topic.created_at, true)}
                </time>
              </Text>
            </Box>
          </Box>
        </Link>
      ))}
      {changelogTopics && (
        <Link
          isExternal
          href="https://bitrise.io/changelog"
          colorScheme="purple"
          marginLeft="48"
          paddingY="16"
          onClick={() => segmentTrack('Changelog Drawer View All Button Clicked')}
        >
          View all <Icon name="ArrowMoveRight" width="16" />
        </Link>
      )}
    </Box>
  );
};

export default ChangelogDrawer;
